import React, { useMemo } from 'react';
import { Redirect, useParams } from 'react-router';
import styled from 'styled-components';
import { ProjectId, SpaceId, SpaceTabId } from 'api/models';
import { Project } from 'api/project';
import { NotFound as NotFoundAtom } from 'components/atoms/result';
import { ProjectSpacesPage } from 'components/pages/project-details/project-spaces/project-spaces';
import { ProjectSpacesSkeleton } from 'components/pages/project-details/project-spaces/project-spaces-skeleton';
import { useSpace } from 'data/spaces/hooks';
import { useSpaceTabHistoryLocalStorageState } from 'hooks/use-space-tab-local-storage-state';
import { useTranslation } from 'hooks/use-translation';
import { useProjectSpacesQuery, useSpaceDetailQuery } from 'queries';
import {
  getProjectPlanUrl,
  getSpaceDetailUrl,
  getSpaceTabDetailUrl,
} from 'utils/urls';

interface ParamTypes {
  projectId: string;
  spaceId: string;
}

const NotFound = styled(NotFoundAtom)`
  padding-top: var(--spacing-12);
`;

export const RedirectToSpaceTabById = ({ project }: { project: Project }) => {
  const { projectId, spaceId } = useParams<ParamTypes>();
  const { value: recentlyVisitedSpace } = useSpaceTabHistoryLocalStorageState();
  const { data: spaceDetails, status } = useSpaceDetailQuery(Number(spaceId), {
    enabled: !Number.isNaN(Number(spaceId)),
  });

  // this is for active tab and will be removed
  useSpace(Number(spaceId));

  const currentSpaceDetails: [string, number | string] = useMemo(() => {
    const spaceTabs = spaceDetails?.spaceTabs ?? [];
    const [, spaceTabIdFromLocal] = recentlyVisitedSpace[projectId] ?? [];

    if (
      spaceTabIdFromLocal &&
      spaceTabs.some((d) => {
        return d.spaceTabId.toString() === spaceTabIdFromLocal;
      })
    ) {
      return [spaceId, spaceTabIdFromLocal];
    }
    if (spaceTabs.length) {
      return [spaceId, spaceTabs[0].spaceTabId];
    }

    return [spaceId, 'new'];
  }, [recentlyVisitedSpace, spaceId, spaceDetails, projectId]);

  const [currentSpaceId, currentSpaceTabId] = currentSpaceDetails;

  switch (status) {
    case 'loading': {
      return <ProjectSpacesSkeleton project={project} />;
    }
    case 'success': {
      return (
        <Redirect
          to={{
            pathname: getSpaceTabDetailUrl(
              SpaceTabId(
                Number(projectId),
                Number(currentSpaceId),
                currentSpaceTabId,
              ),
            ),
          }}
        />
      );
    }
    case 'idle': {
      if (spaceId === 'new') {
        return <ProjectSpacesPage project={project} />;
      }

      return null;
    }

    case 'error':
    default: {
      return <Redirect to="/" />;
    }
  }
};

export const RedirectToSpaceById = ({ project }: { project: Project }) => {
  const actionText = useTranslation('space.404.action');
  const { value: recentlyVisitedSpace } = useSpaceTabHistoryLocalStorageState();

  const { projectId } = useParams<ParamTypes>();
  const { data: spaces = [], status } = useProjectSpacesQuery(
    ProjectId(Number(projectId)),
  );

  const currentSpaceId = useMemo(() => {
    const [spaceIdFromLocal] = recentlyVisitedSpace[projectId] ?? [];

    if (
      spaceIdFromLocal &&
      spaceIdFromLocal !== 'new' &&
      spaces.some((space) => space.spaceId.toString() === spaceIdFromLocal)
    ) {
      return Number(spaceIdFromLocal);
    }

    return spaces[0]?.spaceId;
  }, [spaces, recentlyVisitedSpace, projectId]);

  switch (status) {
    case 'loading': {
      return <ProjectSpacesSkeleton project={project} />;
    }

    case 'success':
    case 'idle': {
      if (spaces.length) {
        return (
          <Redirect
            to={{
              pathname: getSpaceDetailUrl(
                SpaceId(Number(projectId), currentSpaceId),
              ),
            }}
          />
        );
      }

      return (
        <Redirect
          to={{
            pathname: `/projects/${projectId}/spaces/new`,
          }}
        />
      );
    }

    case 'error': {
      return (
        <NotFound
          actionText={actionText}
          linkTo={getProjectPlanUrl(ProjectId(Number(projectId)))}
        />
      );
    }

    default: {
      return <></>;
    }
  }
};
